import {
  Alert,
  DialogStep,
  formatAddress,
  TextFieldSplit,
} from "@inductiveautomation/ws-components";
import { useTranslation } from "react-i18next";
import { useAddressWithFullStateName } from "../../../../hooks/useStateNameLookup";

import { useCurrentUser } from "../../../../hooks/currentUser";
import { getDialogStyles } from "../../_training.styles";
import { PurchaseTestReviewStepProps } from "../../Training.types";
import ReviewSectionHeader from "./review/ReviewSectionHeader";

export const useReviewStep = ({
  isOpen,
  onClose,
  purchaseBarProps,
  onSubmit,
  values,
  purchaseAlert,
  nextLoading,
  certificationType,
  onScrollAway,
  creditsAvailable,
  paymentMethod,
}: PurchaseTestReviewStepProps): DialogStep => {
  const { t: unprefixedT } = useTranslation();
  const translationKeyPrefix =
    certificationType?.toLowerCase() === "gold"
      ? "training.actions.purchase_test.dialog.gold.steps.review_order"
      : "training.actions.purchase_test.dialog.core.steps.review_order";
  const { t } = useTranslation("translation", {
    keyPrefix: translationKeyPrefix,
  });

  const { classes } = getDialogStyles();
  const addressWithStateName = useAddressWithFullStateName(
    values.billingAddress
  );
  const { user } = useCurrentUser();

  return {
    label: t("label"),
    substeps: [],
    nextLabel: unprefixedT(
      "training.actions.purchase_test.dialog.submit_label"
    ),
    nextLoading,
    dialogProps: {
      supportingText: t("supporting_text"),
      isOpen,
      testId: "purchase-test-dialog-review_order",
      showCloseButton: true,
      onClose,
      onScrollAway,
      preNext: onSubmit,
      overline: "",
      headline: t("heading"),
      renderContent: (goToStep) => (
        <>
          <div className={classes.reviewOrderAlertContainer}>
            {purchaseAlert?.description ? (
              <Alert
                id="purchase-test-dialog-review_order-alert"
                variant={purchaseAlert.variant ?? "neutral"}
                title={purchaseAlert.title}
                description={purchaseAlert.description}
                closeButtonEventHandler={() => undefined}
              />
            ) : (
              <Alert
                id="purchase-test-dialog-review_order-alert"
                variant="info"
                description={t("alert_text")}
                closeButtonEventHandler={() => undefined}
              />
            )}
          </div>

          <div className={classes.reviewOrderFieldsContainer}>
            <TextFieldSplit
              id="purchase-test-dialog-review_order-products"
              label={t("products_label")}
              isReadOnly
              value={t("products_value")}
              onChange={() => undefined}
              onChipChange={() => undefined}
              items={[]}
              hasButton
              disabled
              buttonLabel={unprefixedT("common.edit")}
              buttonsAction={() => undefined}
              buttonVariant="text"
              className={classes.reviewOrderTextFieldSplit}
              helperMessage={new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(purchaseBarProps.subTotal ?? 0)}
            />
            {(!creditsAvailable?.length ||
              paymentMethod === "DO_NOT_USE_CREDIT") && (
              <>
                <ReviewSectionHeader name="contact" />
                <TextFieldSplit
                  id="purchase-test-dialog-review_order-personal_information"
                  label={t("personal_information_label")}
                  isReadOnly
                  value={`${user?.firstName} ${user?.lastName}`}
                  disabled
                  onChange={() => undefined}
                  onChipChange={() => undefined}
                  items={[]}
                  hasButton
                  buttonLabel={unprefixedT("common.edit")}
                  buttonVariant="text"
                  className={classes.reviewOrderTextFieldSplit}
                />

                <ReviewSectionHeader name="payment" />
                <TextFieldSplit
                  id="purchase-test-dialog-review_order-credit_card"
                  label={t("credit_card_label")}
                  isReadOnly
                  value={`**** **** **** ${values.creditCard?.last4}`}
                  onChange={() => undefined}
                  onChipChange={() => undefined}
                  items={[]}
                  hasButton
                  buttonLabel={unprefixedT("common.edit")}
                  buttonsAction={() =>
                    goToStep(
                      paymentMethod === "DO_NOT_USE_CREDIT" ? 2 : 1,
                      undefined,
                      true
                    )
                  }
                  buttonVariant="text"
                  className={classes.reviewOrderTextFieldSplit}
                />

                <ReviewSectionHeader name="billing_address" />
                <TextFieldSplit
                  id="purchase-test-dialog-review_order-biling_address"
                  label={t("billing_address_label")}
                  isReadOnly
                  value={
                    addressWithStateName
                      ? formatAddress(addressWithStateName)
                      : "–"
                  }
                  onChange={() => undefined}
                  onChipChange={() => undefined}
                  items={[]}
                  hasButton
                  buttonLabel={unprefixedT("common.edit")}
                  buttonsAction={() =>
                    goToStep(
                      paymentMethod === "DO_NOT_USE_CREDIT" ? 2 : 1,
                      undefined,
                      true
                    )
                  }
                  buttonVariant="text"
                  className={classes.reviewOrderTextFieldSplit}
                />
              </>
            )}
          </div>
        </>
      ),
      purchaseBarProps,
    },
  };
};
